import { useState } from 'react';
import type { SelectableEntity } from '@modo-vivo/engine';
import type { DemoAiBridge, RestaurantSnapshot } from '@modo-vivo/scenario-restaurant';

interface Props {
  selection: SelectableEntity | null;
  snapshot: RestaurantSnapshot | null;
  aiBridge: DemoAiBridge | null;
}

function formatMoney(n: number): string {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  }).format(n);
}

function formatDuration(from?: number): string {
  if (!from) {
    return '—';
  }
  const mins = Math.floor((Date.now() - from) / 60000);
  return `${mins} min (simulado)`;
}

export function InfoPanel({ selection, snapshot, aiBridge }: Props) {
  const [aiSummary, setAiSummary] = useState<string | null>(null);

  if (!snapshot) {
    return (
      <section className="panel-section">
        <p className="empty">Cargando escenario…</p>
      </section>
    );
  }

  if (!selection) {
    return (
      <section className="panel-section">
        <h2>Información</h2>
        <p className="empty">Seleccioná una mesa, zona o empleado en el escenario.</p>
        <p className="hint">Datos marcados como simulación — Orden 001.</p>
      </section>
    );
  }

  const tableNum = selection.kind === 'table' ? Number(selection.id.replace('table-', '')) : null;
  const table = tableNum ? snapshot.tables.find((t) => t.number === tableNum) : undefined;
  const employee = selection.kind === 'employee'
    ? snapshot.employees.find((e) => e.id === selection.id)
    : undefined;

  const requestAiContext = async () => {
    if (!aiBridge) {
      return;
    }
    const scope =
      selection.kind === 'table'
        ? 'table'
        : selection.kind === 'register'
          ? 'register'
          : selection.kind === 'kitchen'
            ? 'kitchen'
            : selection.kind === 'storage'
              ? 'storage'
              : selection.kind === 'employee'
                ? 'employee'
                : 'zone';
    const res = await aiBridge.requestContext({ scope, targetId: selection.id });
    setAiSummary(res.summary);
    aiBridge.applyHighlight({
      targetIds: [selection.id],
      style: 'pulse',
      durationMs: 4000,
      message: res.summary,
    });
  };

  return (
    <section className="panel-section">
      <h2>{selection.label}</h2>
      <p className="hint">Panel informativo · datos ficticios</p>

      {table && (
        <>
          <dl className="kv">
            <dt>Número</dt>
            <dd>{table.number}</dd>
            <dt>Estado</dt>
            <dd>{table.status}</dd>
            <dt>Clientes</dt>
            <dd>{table.customers.length}</dd>
            <dt>Mozo</dt>
            <dd>{table.waiterId ?? 'Sin asignar'}</dd>
            <dt>Pedido</dt>
            <dd>{table.orderId ?? '—'}</dd>
            <dt>Consumo</dt>
            <dd>{formatMoney(table.consumptionTotal)}</dd>
            <dt>Ocupación</dt>
            <dd>{formatDuration(table.occupiedSince)}</dd>
          </dl>
          {table.orderItems.length > 0 && (
            <>
              <h3>Ítems</h3>
              <ul className="list-compact">
                {table.orderItems.map((i) => (
                  <li key={i.name}>
                    {i.quantity}× {i.name} ({formatMoney(i.unitPrice)})
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}

      {selection.kind === 'register' && (
        <dl className="kv">
          <dt>Ventas del día</dt>
          <dd>{formatMoney(snapshot.register.salesToday)}</dd>
          <dt>Operaciones</dt>
          <dd>{snapshot.register.operationCount}</dd>
          <dt>Total cobrado</dt>
          <dd>{formatMoney(snapshot.register.totalCollected)}</dd>
          <dt>Estado caja</dt>
          <dd>{snapshot.register.open ? 'Abierta' : 'Cerrada'}</dd>
          <dt>Efectivo</dt>
          <dd>{formatMoney(snapshot.register.payments.efectivo ?? 0)}</dd>
          <dt>Tarjeta</dt>
          <dd>{formatMoney(snapshot.register.payments.tarjeta ?? 0)}</dd>
        </dl>
      )}

      {selection.kind === 'kitchen' && (
        <>
          <h3>Pendientes</h3>
          <ul className="list-compact">
            {snapshot.kitchen
              .filter((k) => k.status === 'pendiente')
              .map((k) => (
                <li key={k.orderId}>
                  {k.orderId} · Mesa {k.tableNumber}
                </li>
              ))}
          </ul>
          <h3>En preparación</h3>
          <ul className="list-compact">
            {snapshot.kitchen
              .filter((k) => k.status === 'en_preparacion')
              .map((k) => (
                <li key={k.orderId}>
                  {k.orderId} · espera {formatDuration(k.startedAt)}
                </li>
              ))}
          </ul>
          <h3>Terminados</h3>
          <ul className="list-compact">
            {snapshot.kitchen
              .filter((k) => k.status === 'terminado')
              .map((k) => (
                <li key={k.orderId}>{k.orderId}</li>
              ))}
          </ul>
        </>
      )}

      {selection.kind === 'storage' && (
        <>
          <h3>Existencias (ficticias)</h3>
          <ul className="list-compact">
            {snapshot.storage.map((s) => (
              <li key={s.sku}>
                {s.name}: {s.quantity}
                {s.quantity < s.minStock ? ' ⚠ bajo stock' : ''}
              </li>
            ))}
          </ul>
          {snapshot.lastSupplierDelivery && (
            <>
              <h3>Última recepción</h3>
              <p className="hint">
                {snapshot.lastSupplierDelivery.supplierName} ·{' '}
                {new Date(snapshot.lastSupplierDelivery.at).toLocaleString('es-AR')}
              </p>
            </>
          )}
        </>
      )}

      {employee && (
        <dl className="kv">
          <dt>Nombre</dt>
          <dd>{employee.name}</dd>
          <dt>Función</dt>
          <dd>{employee.role}</dd>
          <dt>Estado</dt>
          <dd>{employee.status}</dd>
          <dt>Actividad</dt>
          <dd>{employee.activity}</dd>
        </dl>
      )}

      {selection.kind === 'entrance' && (
        <p className="hint">Punto de ingreso de clientes simulados hacia el salón.</p>
      )}

      {selection.kind === 'supplier' && (
        <p className="hint">Zona de recepción de mercadería ficticia del proveedor.</p>
      )}

      <button type="button" className="action primary" style={{ width: '100%', marginTop: 12 }} onClick={() => void requestAiContext()}>
        Vista IA externa (contexto local)
      </button>
      {aiSummary && <p className="hint" style={{ marginTop: 8 }}>{aiSummary}</p>}
    </section>
  );
}
